/**
 * Connector Import API service.
 *
 * Thin wrapper over:
 *   GET  /api/connectors/:toolkitKey/tree
 *   POST /api/connectors/:toolkitKey/import
 *
 * Consumed by TanStack Query hooks in `hooks/data/useConnectorImport.ts`.
 * Mirror of `connectors.service.ts` — one method per backend endpoint.
 *
 * NOTE: run progress is delivered live via the `/notifications` socket (the
 * tray), so there is no FE caller for `GET /api/connector-import/:runId` — that
 * endpoint stays on the backend for a future "view run" surface (CR-B4).
 */

import { apiService } from './api.service';

// ---------------------------------------------------------------------------
// Types that mirror the backend contract (Task 1.10 / 1.13)
// ---------------------------------------------------------------------------

/** nodeKind discriminant returned by the tree endpoint. */
export type TreeNodeKind = 'container' | 'item';
/** Item payload subtype — documents vs binary files. */
export type TreeItemKind = 'document' | 'file';

/**
 * One node in the lazy connector source tree.
 * Matches `TreeNode` in `packages/backend/src/services/connectors/import/types.ts`.
 */
export interface TreeNode {
  /** Opaque adapter-defined ref (e.g. notebook id, section id). */
  readonly ref: string;
  readonly name: string;
  readonly nodeKind: TreeNodeKind;
  /** Present when nodeKind === 'item'. */
  readonly itemKind?: TreeItemKind;
  /** True → the wizard should offer lazy expansion of this node's children. */
  readonly hasChildren: boolean;
  /** Full path from root to this node (e.g. ["My Notebook", "Section A"]). */
  readonly path: ReadonlyArray<string>;
  /**
   * Canonical provenance id of this node (e.g. OneNote `0-…`), distinct from the
   * opaque `ref`. Present on containers with provenance; used to key the import
   * collection chain so re-imports dedup instead of duplicating.
   */
  readonly externalId?: string;
}

/** One ancestor container in a selection's root→immediate-parent chain. */
export interface ImportSelectionAncestor {
  readonly name: string;
  readonly externalId: string;
}

/**
 * One explicitly-selected tree node sent to start an import. Carries the
 * metadata discovery needs to build a correct, provenance-keyed collection
 * chain WITHOUT re-deriving it from the opaque `ref`.
 *
 * Mirrors `ImportSelection` in
 * `packages/backend/src/services/connectors/import/types.ts`.
 */
export interface ImportSelection {
  readonly ref: string;
  readonly name: string;
  readonly externalId: string;
  readonly nodeKind: TreeNodeKind;
  /** Root → immediate-parent container chain (empty for a root-level node). */
  readonly ancestors: ReadonlyArray<ImportSelectionAncestor>;
}

/** Import run status values. Terminal statuses stop polling. */
export type ConnectorImportStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'COMPLETED_WITH_ERRORS'
  | 'FAILED';

/**
 * A connector import run as returned by `GET /api/connector-import/:runId`.
 * Matches the Prisma select shape in the controller.
 */
export interface ConnectorImportRun {
  readonly id: string;
  readonly workspaceId: string;
  readonly toolkitKey: string;
  readonly status: ConnectorImportStatus;
  readonly targetCollectionId: string | null;
  readonly selectedCount: number;
  readonly succeededCount: number;
  readonly skippedCount: number;
  readonly failedCount: number;
  readonly createdAt: string;
  readonly finishedAt: string | null;
}

// ---------------------------------------------------------------------------
// Completion socket event payload
// ---------------------------------------------------------------------------

/**
 * `data` block from a `connector-import:completed` notification event.
 */
export interface ConnectorImportCompletedData {
  readonly runId: string;
  readonly succeeded: number;
  readonly skipped: number;
  readonly failed: number;
  readonly selected: number;
  readonly status: string;
  readonly targetCollectionId: string | null;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class ConnectorImportService {
  /**
   * Browse the source tree for a connector toolkit.
   * Pass `parentRef` to expand a specific node's children; omit for roots.
   */
  async listTree(toolkitKey: string, parentRef?: string) {
    const url = parentRef
      ? `/connectors/${encodeURIComponent(toolkitKey)}/tree?parentRef=${encodeURIComponent(parentRef)}`
      : `/connectors/${encodeURIComponent(toolkitKey)}/tree`;
    return apiService.get<TreeNode[]>(url);
  }

  /**
   * Start a connector import run. Returns 202 with `{ runId }`.
   */
  async startImport(
    toolkitKey: string,
    body: { selectedNodes: ImportSelection[]; targetCollectionId?: string },
  ) {
    return apiService.post<{ runId: string }>(
      `/connectors/${encodeURIComponent(toolkitKey)}/import`,
      body,
    );
  }
}

export const connectorImportService = new ConnectorImportService();
